/**
 * Benvel filter bottom-sheet controller
 * Story 3.1 — Apparel-relevant PLP filters
 *
 * Responsibilities:
 *   1. Open / close the native <dialog> bottom sheet.
 *   2. Manage staged filter state (selections accumulate until "Aplicar").
 *   3. On "Aplicar": build a filter URL and dispatch to Horizon's facets-form-component.
 *   4. On "Limpiar": clear staged state inside the sheet.
 *   5. On individual top-bar pill ✕: remove that single filter and re-render.
 *   6. Sync top-bar active pills when the section re-renders (URL-driven).
 *
 * Focus trap inside the dialog is provided by the native <dialog> element.
 * Esc-to-close is also handled by the browser natively.
 *
 * SRA integration: after building the URL from staged state, we call
 * facets-form-component.updateFiltersByURL(url) which already wraps
 * sectionRenderer.renderSection via Horizon's existing pattern.
 */

(function () {
  'use strict';

  /**
   * BenvelFilterSheet
   * Custom element that wraps the <dialog> bottom sheet and its controls.
   * Expected HTML shape (see main-collection.liquid):
   *
   *   <benvel-filter-sheet
   *     data-section-id="shopify-section-…"
   *     data-facets-form-id="BenvelFacetsForm-…"
   *   >
   *     <button class="benvel-filter-bar__trigger" data-action="open-sheet">Filtrar</button>
   *     <dialog class="benvel-filter-sheet" aria-labelledby="bvfs-title-…">
   *       …chip groups with data-filter-name / data-filter-value…
   *       <button data-action="apply">Aplicar</button>
   *       <button data-action="clear-all">Limpiar</button>
   *       <button data-action="close-sheet">✕</button>
   *     </dialog>
   *     <div class="benvel-filter-bar">…active pills with data-remove-url…</div>
   *   </benvel-filter-sheet>
   */
  class BenvelFilterSheet extends HTMLElement {
    /** @type {HTMLDialogElement|null} */
    #dialog = null;
    /** @type {HTMLButtonElement|null} */
    #trigger = null;
    /** @type {Map<string, Set<string>>} staged filter selections (name → values) */
    #staged = new Map();

    connectedCallback() {
      this.#dialog = this.querySelector('dialog.benvel-filter-sheet');
      this.#trigger = this.querySelector('[data-action="open-sheet"]');

      if (!this.#dialog || !this.#trigger) return;

      // Wire up open trigger
      this.#trigger.addEventListener('click', this.#openSheet.bind(this));

      // Wire up sheet controls via event delegation
      this.#dialog.addEventListener('click', this.#handleDialogClick.bind(this));

      // Close on backdrop click (native dialog backdrop is ::backdrop, click on dialog itself = backdrop)
      this.#dialog.addEventListener('click', this.#handleBackdropClick.bind(this));

      // Initialise staged state from current URL
      this.#syncStagedFromURL();

      // Keep top-bar pills in sync after SRA re-renders
      document.addEventListener('theme:filter:update', this.#onFilterUpdate.bind(this));
    }

    disconnectedCallback() {
      document.removeEventListener('theme:filter:update', this.#onFilterUpdate.bind(this));
    }

    // -----------------------------------------------------------------------
    // Sheet open / close
    // -----------------------------------------------------------------------

    #openSheet() {
      if (!this.#dialog) return;
      // Sync staged state from the chip button states before opening
      this.#syncStagedFromChips();
      this.#dialog.showModal();
      // Move focus to first interactive element inside dialog
      const firstEl = this.#dialog.querySelector('button, input, [tabindex]');
      if (firstEl instanceof HTMLElement) {
        // Small rAF delay so the dialog open animation doesn't conflict with focus
        requestAnimationFrame(() => firstEl.focus());
      }
    }

    #closeSheet() {
      if (!this.#dialog) return;
      this.#dialog.close();
      // Return focus to the trigger button
      if (this.#trigger instanceof HTMLElement) {
        this.#trigger.focus();
      }
    }

    #handleBackdropClick(event) {
      // A click directly on the dialog element (not a child) means the backdrop was tapped
      if (event.target === this.#dialog) {
        // Revert staged state (close without applying)
        this.#syncStagedFromURL();
        this.#syncChipsFromStaged();
        this.#closeSheet();
      }
    }

    // -----------------------------------------------------------------------
    // Chip interaction
    // -----------------------------------------------------------------------

    #handleDialogClick(event) {
      if (!(event.target instanceof HTMLElement)) return;

      const action = event.target.closest('[data-action]')?.dataset.action;
      const chip = event.target.closest('.benvel-filter-chip');

      if (action === 'apply') {
        this.#applyFilters();
        return;
      }

      if (action === 'clear-all') {
        this.#clearAllStaged();
        return;
      }

      if (action === 'close-sheet') {
        // Close without applying — revert staged state
        this.#syncStagedFromURL();
        this.#syncChipsFromStaged();
        this.#closeSheet();
        return;
      }

      if (chip instanceof HTMLElement) {
        this.#toggleChip(chip);
      }
    }

    /**
     * Toggle the staged state for a chip and update its visual state.
     * @param {HTMLElement} chip
     */
    #toggleChip(chip) {
      const filterName = chip.dataset.filterName || '';
      const filterValue = chip.dataset.filterValue || '';
      if (!filterName || !filterValue) return;

      const isPressed = chip.getAttribute('aria-pressed') === 'true';
      const newState = !isPressed;

      chip.setAttribute('aria-pressed', newState.toString());

      if (!this.#staged.has(filterName)) {
        this.#staged.set(filterName, new Set());
      }
      const values = /** @type {Set<string>} */ (this.#staged.get(filterName));

      if (newState) {
        values.add(filterValue);
      } else {
        values.delete(filterValue);
        if (values.size === 0) {
          this.#staged.delete(filterName);
        }
      }
    }

    // -----------------------------------------------------------------------
    // Apply / clear
    // -----------------------------------------------------------------------

    #applyFilters() {
      const facetsForm = this.#getFacetsForm();
      if (!facetsForm) return;

      const url = this.#buildURLFromStaged();
      facetsForm.updateFiltersByURL(url.toString());
      this.#closeSheet();
    }

    #clearAllStaged() {
      this.#staged.clear();
      this.#syncChipsFromStaged();
    }

    // -----------------------------------------------------------------------
    // URL / state helpers
    // -----------------------------------------------------------------------

    /**
     * Build a URL from the current staged state, preserving non-filter params.
     * @returns {URL}
     */
    #buildURLFromStaged() {
      const url = new URL(window.location.href);

      // Remove all existing filter params
      const keysToDelete = [];
      for (const key of url.searchParams.keys()) {
        if (key.startsWith('filter.')) {
          keysToDelete.push(key);
        }
      }
      keysToDelete.forEach((k) => url.searchParams.delete(k));

      // Re-add from staged state
      for (const [name, values] of this.#staged.entries()) {
        for (const value of values) {
          url.searchParams.append(name, value);
        }
      }

      // Reset pagination
      url.searchParams.delete('page');

      return url;
    }

    /**
     * Sync staged state from the current URL (used on init and on revert).
     */
    #syncStagedFromURL() {
      this.#staged.clear();
      const params = new URL(window.location.href).searchParams;

      for (const [key, value] of params.entries()) {
        if (key.startsWith('filter.')) {
          if (!this.#staged.has(key)) {
            this.#staged.set(key, new Set());
          }
          /** @type {Set<string>} */ (this.#staged.get(key)).add(value);
        }
      }
    }

    /**
     * Sync staged state from chip aria-pressed states
     * (called before opening the sheet so the sheet reflects any top-bar pill removals).
     */
    #syncStagedFromChips() {
      this.#staged.clear();
      if (!this.#dialog) return;

      this.#dialog.querySelectorAll('.benvel-filter-chip[data-filter-name]').forEach((chip) => {
        if (!(chip instanceof HTMLElement)) return;
        if (chip.getAttribute('aria-pressed') !== 'true') return;
        const name = chip.dataset.filterName || '';
        const value = chip.dataset.filterValue || '';
        if (!name || !value) return;
        if (!this.#staged.has(name)) this.#staged.set(name, new Set());
        /** @type {Set<string>} */ (this.#staged.get(name)).add(value);
      });
    }

    /**
     * Update chip aria-pressed states to match the staged map.
     */
    #syncChipsFromStaged() {
      if (!this.#dialog) return;

      this.#dialog.querySelectorAll('.benvel-filter-chip[data-filter-name]').forEach((chip) => {
        if (!(chip instanceof HTMLElement)) return;
        const name = chip.dataset.filterName || '';
        const value = chip.dataset.filterValue || '';
        const isSelected = this.#staged.has(name) && /** @type {Set<string>} */ (this.#staged.get(name)).has(value);
        chip.setAttribute('aria-pressed', isSelected.toString());
      });
    }

    /**
     * After SRA re-render, re-sync staged state from the new URL.
     */
    #onFilterUpdate() {
      this.#syncStagedFromURL();
    }

    /**
     * Get the Horizon FacetsFormComponent instance by ID.
     * @returns {HTMLElement|null}
     */
    #getFacetsForm() {
      const formId = this.dataset.facetsFormId;
      if (!formId) return null;
      return document.getElementById(formId);
    }
  }

  if (!customElements.get('benvel-filter-sheet')) {
    customElements.define('benvel-filter-sheet', BenvelFilterSheet);
  }

  /**
   * BenvelFilterPill
   * Custom element wrapping a top-bar active-filter pill.
   * Clicking removes that single filter and triggers SRA re-render.
   *
   * Expected attribute: data-remove-url (the URL with that filter removed)
   */
  class BenvelFilterPill extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.#removePill.bind(this));
      this.addEventListener('keydown', this.#onKeyDown.bind(this));
    }

    #onKeyDown(event) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.#removePill();
      }
    }

    #removePill() {
      const url = this.dataset.removeUrl;
      if (!url) return;

      const filterSheet = this.closest('benvel-filter-sheet');
      const formId = filterSheet?.dataset.facetsFormId;
      const facetsForm = formId ? document.getElementById(formId) : null;

      if (facetsForm && typeof facetsForm['updateFiltersByURL'] === 'function') {
        facetsForm['updateFiltersByURL'](url);
      } else {
        // Fallback: navigate to the remove URL
        window.location.href = url;
      }
    }
  }

  if (!customElements.get('benvel-filter-pill')) {
    customElements.define('benvel-filter-pill', BenvelFilterPill);
  }
})();
